import React, { Fragment, useState, useCallback, useEffect } from 'react';
import Link from 'shared/SlashLink';
import usePageSeo from '../../hooks/usePageSeo';
import Header from '../../components/header/Header';
import PageTitle from '../../components/pagetitle/PageTitle';
import Scrollbar from '../../components/scrollbar/scrollbar';
import Footer from '../../components/footer/Footer';
import CtaSection from '../../components/CtaSection/CtaSection';
import ToolLeadCapture from '../../components/ToolLeadCapture/ToolLeadCapture';
import { PAGESPEED_API_KEY } from '../../config/pagespeed';
import { trackEvent } from '../../utils/analytics';

// ---------- PageSpeed Insights (Lighthouse) — client-side, CORS-enabled ----------
const CATEGORIES = [
    { id: 'performance', label: 'Performance' },
    { id: 'seo', label: 'SEO' },
    { id: 'accessibility', label: 'Accessibility' },
    { id: 'best-practices', label: 'Best Practices' },
];
const METRIC_IDS = [
    ['largest-contentful-paint', 'LCP'],
    ['cumulative-layout-shift', 'CLS'],
    ['total-blocking-time', 'TBT'],
    ['first-contentful-paint', 'FCP'],
    ['speed-index', 'Speed Index'],
];

const psiUrl = (url, strategy) =>
    `https://www.googleapis.com/pagespeedonline/v5/runPagespeed?url=${encodeURIComponent(url)}` +
    `&strategy=${strategy}&category=performance&category=seo&category=accessibility&category=best-practices` +
    (PAGESPEED_API_KEY ? `&key=${PAGESPEED_API_KEY}` : '');

const clean = (s = '') =>
    s.replace(/\[([^\]]+)\]\([^)]*\)/g, '$1').replace(/`([^`]+)`/g, '$1').replace(/\s+/g, ' ').trim();

const bandColor = (score) =>
    score == null ? '#9a93a3' : score >= 90 ? '#1f9d57' : score >= 50 ? '#E08A2C' : '#D14343';
const bandLabel = (score) =>
    score == null ? '—' : score >= 90 ? 'Good' : score >= 50 ? 'Needs work' : 'Poor';

function parseResult(data) {
    const lh = data.lighthouseResult || {};
    const audits = lh.audits || {};
    const cats = CATEGORIES.map(({ id, label }) => {
        const c = (lh.categories || {})[id];
        const score = c && c.score != null ? Math.round(c.score * 100) : null;
        const issues = (c?.auditRefs || [])
            .map((ref) => audits[ref.id])
            .filter((a) => a && a.score != null && a.score < 0.9 &&
                ['binary', 'numeric', 'metricSavings'].includes(a.scoreDisplayMode))
            .map((a) => ({
                id: a.id, title: a.title, desc: clean(a.description),
                value: a.displayValue || '', sev: a.score < 0.5 ? 'fail' : 'warn',
            }))
            .sort((a, b) => (a.sev === b.sev ? 0 : a.sev === 'fail' ? -1 : 1));
        return { id, label, score, issues };
    });
    const have = cats.filter((c) => c.score != null);
    const overall = have.length ? Math.round(have.reduce((s, c) => s + c.score, 0) / have.length) : null;
    const metrics = METRIC_IDS.map(([id, label]) => {
        const a = audits[id];
        return a ? { label, value: a.displayValue || '—', sev: a.score == null ? 'na' : a.score < 0.5 ? 'fail' : a.score < 0.9 ? 'warn' : 'pass' } : null;
    }).filter(Boolean);
    const totalIssues = cats.reduce((n, c) => n + c.issues.length, 0);
    return { overall, cats, metrics, totalIssues, finalUrl: lh.finalUrl || lh.requestedUrl };
}

const friendlyError = (msg = '') => {
    if (/quota|rate|429|limit/i.test(msg)) return 'We’re getting a lot of audit requests right now. Wait a minute and try again — or add a free PageSpeed API key for unlimited runs.';
    if (/unable to|lighthouse|fetch|FAILED|ERRORED|DNS|reach/i.test(msg)) return 'Couldn’t fully load that site. Make sure it’s a public, live URL (e.g. https://example.com) and try again.';
    return msg || 'Something went wrong running the audit. Please try again.';
};

const Ring = ({ score, size = 132, stroke = 11 }) => {
    const r = (size - stroke) / 2, circ = 2 * Math.PI * r, pct = score == null ? 0 : score / 100;
    const col = bandColor(score);
    return (
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label={`Score ${score == null ? 'unavailable' : score + ' out of 100'}`}>
            <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth={stroke} />
            <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={col} strokeWidth={stroke} strokeLinecap="round"
                strokeDasharray={circ} strokeDashoffset={circ * (1 - pct)} transform={`rotate(-90 ${size / 2} ${size / 2})`} />
            <text x="50%" y="50%" dominantBaseline="central" textAnchor="middle" fontSize={size * 0.3} fontWeight="800" fill={col}>
                {score == null ? '—' : score}
            </text>
        </svg>
    );
};

const sevDot = (sev) => ({ fail: '#D14343', warn: '#E08A2C', pass: '#1f9d57', na: '#9a93a3' }[sev] || '#9a93a3');

const CategoryCard = ({ cat }) => {
    const [open, setOpen] = useState(cat.id === 'seo' || cat.id === 'performance');
    return (
        <div style={{ background: '#fff', border: '1px solid var(--bs-border-color)', borderRadius: 14, marginBottom: 16, overflow: 'hidden' }}>
            <button type="button" onClick={() => setOpen((o) => !o)}
                style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 14, padding: '16px 20px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left' }}
                aria-expanded={open}>
                <span style={{ flex: '0 0 auto', width: 46, height: 46, borderRadius: 10, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: bandColor(cat.score), color: '#fff', fontWeight: 800, fontSize: '1.05rem' }}>
                    {cat.score == null ? '—' : cat.score}
                </span>
                <span style={{ flex: 1 }}>
                    <span style={{ fontWeight: 700, color: 'var(--bs-dark)' }}>{cat.label}</span>
                    <span style={{ display: 'block', fontSize: '.82rem', color: 'var(--bs-secondary-color)' }}>
                        {cat.issues.length === 0 ? 'No issues flagged' : `${cat.issues.length} issue${cat.issues.length === 1 ? '' : 's'} to review`}
                    </span>
                </span>
                <i className={`fa-solid fa-chevron-${open ? 'up' : 'down'}`} aria-hidden="true" style={{ color: 'var(--bs-secondary-color)', fontSize: '.8rem' }}></i>
            </button>
            {open && cat.issues.length > 0 && (
                <ul style={{ listStyle: 'none', margin: 0, padding: '0 20px 12px' }}>
                    {cat.issues.map((it) => (
                        <li key={it.id} style={{ display: 'flex', gap: 12, padding: '12px 0', borderTop: '1px solid var(--bs-border-color)' }}>
                            <span style={{ flex: '0 0 auto', width: 9, height: 9, borderRadius: '50%', background: sevDot(it.sev), marginTop: 6 }} aria-hidden="true"></span>
                            <span>
                                <span style={{ fontWeight: 600, color: 'var(--bs-dark)', fontSize: '.92rem' }}>{it.title}</span>
                                {it.value && <span style={{ color: sevDot(it.sev), fontWeight: 700, fontSize: '.85rem', marginLeft: 8 }}>{it.value}</span>}
                                {it.desc && <span style={{ display: 'block', fontSize: '.84rem', color: 'var(--bs-secondary-color)', marginTop: 2 }}>{it.desc}</span>}
                            </span>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

const WHAT_WE_CHECK = [
    'Page titles, meta descriptions & crawlability',
    'Core Web Vitals & page-load performance',
    'Mobile-friendliness & responsive design',
    'Accessibility (contrast, labels, structure)',
    'HTTPS, security & best practices',
    'Indexability, structured data & more',
];

const WebsiteAuditPage = () => {
    usePageSeo({
        title: 'Free Website Audit Tool — SEO, Speed, Meta & Accessibility',
        description: 'Run a free instant website audit. Get SEO, performance (Core Web Vitals), accessibility, meta-tag and best-practice scores for any URL — with a prioritized list of fixes. By BUILDLAB.',
        keywords: 'free website audit, SEO audit tool, website grader, page speed test, core web vitals checker, meta tag checker, site health check, BUILDLAB',
    });

    const [url, setUrl] = useState('');
    const [strategy, setStrategy] = useState('mobile');
    const [status, setStatus] = useState('idle'); // idle | loading | done | error
    const [result, setResult] = useState(null);
    const [error, setError] = useState('');

    const run = useCallback(async (e, override) => {
        if (e) e.preventDefault();
        let target = (override || url).trim();
        if (!target) return;
        if (!/^https?:\/\//i.test(target)) target = 'https://' + target;
        setStatus('loading'); setError(''); setResult(null);
        trackEvent('website_audit_run', { strategy });
        try {
            const res = await fetch(psiUrl(target, strategy));
            const data = await res.json();
            if (data.error) throw new Error(data.error.message);
            if (!data.lighthouseResult) throw new Error('No results returned for that URL.');
            const parsed = parseResult(data);
            setResult(parsed); setStatus('done');
            trackEvent('website_audit_result', { overall: parsed.overall, strategy });
        } catch (err) {
            setError(friendlyError(err.message)); setStatus('error');
        }
    }, [url, strategy]);

    // Deep-link support: /website-audit?url=example.com (from the homepage band) auto-runs the scan.
    useEffect(() => {
        const p = new URLSearchParams(window.location.search).get('url');
        if (p) { setUrl(p); run(null, p); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Fragment>
            <Header />
            <main id="main-content" className="page_content">
                <PageTitle pageTitle={'Free Website Audit'} pagesub={'Tool'} pageTop={'Web & SEO'} />
                <section className="section_space bg-light">
                    <div className="container">
                        <div className="row mb-4">
                            <div className="col-lg-9">
                                <h2 className="mb-3">How healthy is your website? Get an instant audit.</h2>
                                <p>
                                    Enter any URL and we’ll run a full <strong>SEO, performance, accessibility, and best-practices</strong> scan
                                    — powered by Google Lighthouse — then turn the findings into a prioritized fix list. Free, instant, no signup to see your scores.
                                </p>
                            </div>
                        </div>

                        {/* Input */}
                        <form onSubmit={run} style={{ background: '#fff', border: '1px solid var(--bs-border-color)', borderRadius: 16, padding: '22px 24px', marginBottom: 28 }}>
                            <label htmlFor="audit-url" style={{ fontWeight: 600, fontSize: '.9rem', display: 'block', marginBottom: 8 }}>Website URL</label>
                            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                                <input id="audit-url" type="text" value={url} onChange={(e) => setUrl(e.target.value)}
                                    placeholder="example.com" autoComplete="off"
                                    style={{ flex: '1 1 320px', minWidth: 0, border: '1px solid var(--bs-border-color)', borderRadius: 10, padding: '12px 14px', fontSize: '1rem' }} />
                                <div role="group" aria-label="Device" style={{ display: 'inline-flex', border: '1px solid var(--bs-border-color)', borderRadius: 10, overflow: 'hidden' }}>
                                    {['mobile', 'desktop'].map((s) => (
                                        <button key={s} type="button" onClick={() => setStrategy(s)}
                                            className={`btn btn-sm ${strategy === s ? 'btn-primary' : 'btn-light'}`}
                                            style={{ borderRadius: 0, textTransform: 'capitalize', minWidth: 86 }} aria-pressed={strategy === s}>{s}</button>
                                    ))}
                                </div>
                                <button type="submit" className="btn btn-primary" disabled={status === 'loading' || !url.trim()}>
                                    <span className="btn_label" data-text="Audit my site">{status === 'loading' ? 'Auditing…' : 'Audit my site'}</span>
                                </button>
                            </div>
                            {status === 'idle' && (
                                <div className="row mt-3" style={{ rowGap: 6 }}>
                                    {WHAT_WE_CHECK.map((w) => (
                                        <div className="col-md-6" key={w} style={{ fontSize: '.85rem', color: 'var(--bs-secondary-color)' }}>
                                            <i className="fa-solid fa-circle-check" aria-hidden="true" style={{ color: 'var(--bs-primary)', marginRight: 8 }}></i>{w}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </form>

                        {status === 'loading' && (
                            <div style={{ textAlign: 'center', padding: '40px 0' }}>
                                <div className="spinner-border text-primary" role="status" style={{ width: 48, height: 48 }}><span className="visually-hidden">Loading…</span></div>
                                <p className="mt-3 mb-0" style={{ fontWeight: 600 }}>Running a full Lighthouse audit…</p>
                                <p style={{ fontSize: '.85rem', color: 'var(--bs-secondary-color)' }}>This takes 20–40 seconds — we’re loading and scoring the page like a real visitor.</p>
                            </div>
                        )}

                        {status === 'error' && (
                            <div className="alert alert-warning" role="alert" style={{ borderRadius: 12 }}>{error}</div>
                        )}

                        {status === 'done' && result && (
                            <Fragment>
                                {/* Summary (dark) */}
                                <div style={{ background: 'var(--bs-dark)', borderRadius: 18, padding: '30px 32px', color: '#fff', marginBottom: 28 }}>
                                    <div className="row align-items-center">
                                        <div className="col-md-auto text-center mb-3 mb-md-0">
                                            <Ring score={result.overall} />
                                            <div style={{ fontWeight: 700, color: bandColor(result.overall), marginTop: 6 }}>{bandLabel(result.overall)}</div>
                                        </div>
                                        <div className="col">
                                            <div style={{ fontSize: '.82rem', opacity: 0.7 }}>Audit results for</div>
                                            <div style={{ fontWeight: 700, wordBreak: 'break-all', marginBottom: 14 }}>{result.finalUrl}</div>
                                            <div className="row" style={{ rowGap: 12 }}>
                                                {result.cats.map((c) => (
                                                    <div className="col-6 col-lg-3" key={c.id}>
                                                        <div style={{ fontSize: '2rem', fontWeight: 800, lineHeight: 1, color: bandColor(c.score) }}>{c.score == null ? '—' : c.score}</div>
                                                        <div style={{ fontSize: '.8rem', opacity: 0.8 }}>{c.label}</div>
                                                    </div>
                                                ))}
                                            </div>
                                            <p style={{ fontSize: '.86rem', opacity: 0.85, margin: '14px 0 0' }}>
                                                {result.totalIssues === 0
                                                    ? 'Clean scan — no major issues flagged. We can verify with a deeper audit.'
                                                    : `We found ${result.totalIssues} issue${result.totalIssues === 1 ? '' : 's'} worth fixing, sorted by priority below.`}
                                            </p>
                                            <Link to="/contact" className="btn btn-primary mt-3" style={{ marginRight: 10 }}>
                                                <span className="btn_label" data-text="Get a free expert review">Get a free expert review</span>
                                            </Link>
                                            <ToolLeadCapture
                                                tool="Website Audit"
                                                summary={`${result.finalUrl} (${strategy}) — overall ${result.overall}/100 · ` +
                                                    result.cats.map((c) => `${c.label} ${c.score ?? '—'}`).join(', ') +
                                                    ` · ${result.totalIssues} issues`}
                                                emailLabel="Email me the full report"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Core Web Vitals / metrics */}
                                {result.metrics.length > 0 && (
                                    <div style={{ background: '#fff', border: '1px solid var(--bs-border-color)', borderRadius: 14, padding: '18px 22px', marginBottom: 24 }}>
                                        <div style={{ fontWeight: 700, marginBottom: 12, color: 'var(--bs-dark)' }}>Core Web Vitals &amp; speed metrics <span style={{ fontWeight: 400, fontSize: '.82rem', color: 'var(--bs-secondary-color)' }}>({strategy})</span></div>
                                        <div className="row" style={{ rowGap: 12 }}>
                                            {result.metrics.map((m) => (
                                                <div className="col-6 col-md" key={m.label} style={{ textAlign: 'center' }}>
                                                    <div style={{ fontSize: '1.35rem', fontWeight: 800, color: sevDot(m.sev) }}>{m.value}</div>
                                                    <div style={{ fontSize: '.78rem', color: 'var(--bs-secondary-color)' }}>{m.label}</div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Issues by category */}
                                <h3 className="details_item_info_title mb-3">Findings &amp; fixes</h3>
                                {result.cats.map((c) => <CategoryCard key={c.id} cat={c} />)}
                            </Fragment>
                        )}

                        <p className="small text-muted mt-3" style={{ maxWidth: 920 }}>
                            Audit data is generated by Google Lighthouse / PageSpeed Insights and reflects a single page at the time of the scan.
                            It’s a fast health check, not a full technical SEO audit. BUILDLAB builds fast, SEO-ready websites and runs deeper audits —
                            covering meta, structured data, content, and off-page signals — for California businesses.
                        </p>
                    </div>
                </section>
                <CtaSection />
            </main>
            <Footer />
            <Scrollbar />
        </Fragment>
    );
};

export default WebsiteAuditPage;
